import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Routes } from 'src/router/Routes';
import { authStore } from 'src/stores/AuthStore';
import { isSmallMobileDeviceQuery } from 'src/util/responsive';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaidIcon from '@mui/icons-material/Paid';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleIcon from '@mui/icons-material/People';
import { IconButton, Menu, MenuItem, useMediaQuery } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import { ChangePasswordDialog } from '../dialogs/ChangePasswordDialog';
import { UserProfileDialog } from '../dialogs/UserProfileDialog';
import { Colors } from '../utils/Theme';
import { LanguageSelection } from './LanguageSelection';
import { useAppConfig } from 'src/hooks/useAppConfig';
import { useUser } from '../../hooks/useUser';

const drawerWidth = 180;

interface Props {
  children: React.ReactNode;
}

export const LaunchpadDrawer: FC<Props> = ({ children }) => {
  const [openUserProfile, setOpenUserProfile] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const location = useLocation();
  const appConfig = useAppConfig();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  const isSmallMobileDevice = useMediaQuery(isSmallMobileDeviceQuery);
  const [showDrawer, setShowDrawer] = useState(false);
  const user = useUser();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const logout = () => {
    authStore.logout();
    handleClose();
  };

  const showUserProfile = () => {
    setOpenUserProfile(true);
    handleClose();
  };

  const onCloseUserProfile = (action?: 'changePassword') => {
    if (action === 'changePassword') {
      setOpenChangePassword(true);
    }
    setOpenUserProfile(false);
  };

  const menuItems = [
    {
      label: t('drawer.menuitems.assets'),
      icon: <ApartmentOutlinedIcon />,
      path: Routes.ASSETS.ROOT,
    },
    {
      label: t('drawer.menuitems.issuer'),
      icon: <PeopleIcon />,
      path: Routes.ISSUER.ROOT,
    },
    {
      label: t('drawer.menuitems.affiliate'),
      icon: <Diversity2Icon />,
      path: Routes.AFFILIATE.ROOT,
    },
    {
      label: t('drawer.menuitems.partner'),
      icon: <HandshakeIcon />,
      path: Routes.PARTNER.ROOT,
    },
  ];
  if (user?.partner?.assetType.includes('powerplantPortfolio')) {
    menuItems.push({
      label: t('revenuePools.overview.routeLabel'),
      icon: <PaidIcon />,
      path: Routes.REVENUE_POOLS.ROOT,
    });
  }
  if (appConfig?.featureFlags?.manualPayment) {
    menuItems.push({
      label: t('drawer.menuitems.openTransactions'),
      icon: <ReceiptIcon />,
      path: Routes.TRANSACTIONS.ROOT,
    });
  } else {
    menuItems.push({
      label: t('drawer.menuitems.paymentAccount'),
      icon: <AccountBalanceIcon />,
      path: Routes.PAYMENT.ROOT,
    });
  }

  return (
    <>
      <ChangePasswordDialog open={openChangePassword} onClose={() => setOpenChangePassword(false)} />
      <UserProfileDialog open={openUserProfile} onClose={onCloseUserProfile} />
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <CssBaseline />
        <AppBar
          elevation={0}
          position="fixed"
          sx={{
            color: 'black',
            backgroundColor: 'white',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <Toolbar>
            {isSmallMobileDevice && (
              <IconButton size="large" edge="start" color="inherit" onClick={() => setShowDrawer(!showDrawer)}>
                <MenuIcon />
              </IconButton>
            )}
            <Link style={{ zIndex: 100 }} to={Routes.ASSETS.ROOT}>
              <img style={{ cursor: 'pointer' }} src="/logo.svg" alt="brickwise-logo" />
            </Link>

            <div style={{ flexGrow: 1 }}></div>

            <LanguageSelection />
            <IconButton onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              <MenuItem onClick={showUserProfile}>{t('drawer.userprofile')}</MenuItem>
              <MenuItem onClick={logout}>{t('drawer.logout')}</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Drawer
          BackdropProps={{ invisible: true }}
          open={showDrawer}
          variant={isSmallMobileDevice ? 'temporary' : 'permanent'}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            ['& .MuiDrawer-paper']: { width: drawerWidth, boxSizing: 'border-box' },
          }}
        >
          <Toolbar />
          <Box sx={{ overflow: 'auto' }}>
            <List>
              {menuItems.map((item, index: number) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton to={item.path} component={Link}>
                    <ListItemIcon
                      sx={{
                        color: location.pathname.startsWith(item.path) ? Colors.SECONDARY_COLOR : Colors.BLACK_BLUE,
                        opacity: location.pathname.startsWith(item.path) ? 1 : 0.8,
                        minWidth: 40,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        color: location.pathname.startsWith(item.path) ? Colors.SECONDARY_COLOR : Colors.BLACK_BLUE,
                        opacity: location.pathname.startsWith(item.path) ? 1 : 0.8,
                      }}
                      primary={item.label}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Toolbar />
          {children}
        </Box>
      </Box>
    </>
  );
};
